// Helpers puros del organigrama de clientes (spec 032).

// Valor del selector "¿A quién reporta?" que significa "Nadie (cabeza del organigrama)".
// No se usa null porque Vuetify lo trata como "sin selección".
export const HEAD_OPTION = 'head';

export const ARTIFACT_COUNTRIES = ['CL', 'PE', 'MX', 'ES', 'CO'];

const FRIENDLY_MESSAGES = {
  ORG_CHART_CYCLE: 'No puede reportar a alguien que está debajo suyo.',
  MANAGER_OTHER_COMPANY: 'El jefe tiene que ser de la misma empresa.',
  CANDIDATE_ALREADY_DECIDED: 'Alguien ya revisó este contacto.',
  COMPANY_NOT_LINKED: 'Primero hay que vincular la empresa a Pipedrive.'
};

export function isPipedriveError(code) {
  return typeof code === 'string' && code.startsWith('PIPEDRIVE_');
}

// Mensaje para mostrar ante un error del service. Los errores de Pipedrive ya
// vienen del backend en español y sin jerga, así que se usan tal cual.
export function errorMessage(res, fallback = 'Algo falló. Intenta de nuevo.') {
  if (!res) return fallback;
  if (isPipedriveError(res.code)) return res.message || fallback;
  return FRIENDLY_MESSAGES[res.code] || res.message || fallback;
}

// Arma la jerarquía a partir de la lista plana (managerPersonId).
// Una persona cuyo jefe no está en la lista se trata como cabeza.
// Devuelve [{ person, depth, children: [...] }], ordenado por nombre.
export function buildTree(nodes = []) {
  const byId = new Map(nodes.map(person => [person.personId, person]));
  const childrenOf = new Map();
  const roots = [];

  nodes.forEach(person => {
    const managerId = person.managerPersonId;
    if (managerId === null || managerId === undefined || !byId.has(managerId) || managerId === person.personId) {
      roots.push(person);
      return;
    }
    if (!childrenOf.has(managerId)) childrenOf.set(managerId, []);
    childrenOf.get(managerId).push(person);
  });

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const visited = new Set();

  function toBranch(person, depth) {
    visited.add(person.personId);
    const children = (childrenOf.get(person.personId) || [])
      .filter(child => !visited.has(child.personId))
      .sort(byName)
      .map(child => toBranch(child, depth + 1));
    return { person, depth, children };
  }

  return roots.sort(byName).map(person => toBranch(person, 0));
}

// Ids de todas las personas que reportan (directa o indirectamente) a personId.
export function collectDescendantIds(nodes = [], personId) {
  const result = new Set();
  const pending = [personId];
  while (pending.length) {
    const current = pending.pop();
    nodes.forEach(person => {
      if (person.managerPersonId === current && !result.has(person.personId) && person.personId !== personId) {
        result.add(person.personId);
        pending.push(person.personId);
      }
    });
  }
  return result;
}

// Ids de los jefes de personId, desde el directo hacia arriba.
export function collectAncestorIds(nodes = [], personId) {
  const byId = new Map(nodes.map(person => [person.personId, person]));
  const result = [];
  let current = byId.get(personId);
  while (current && current.managerPersonId !== null && current.managerPersonId !== undefined) {
    if (result.includes(current.managerPersonId)) break;
    result.push(current.managerPersonId);
    current = byId.get(current.managerPersonId);
  }
  return result;
}

// Traduce el valor de los campos de ubicación al body del backend.
// manager vacío = no ubicar (null); HEAD_OPTION = cabeza del organigrama.
export function toPlacement({ manager, area } = {}) {
  if (manager === null || manager === undefined || manager === '') return null;
  return {
    managerPersonId: manager === HEAD_OPTION ? null : manager,
    area: area || null
  };
}
